import React from "react"
import TextField from "@mui/material/TextField"
import Autocomplete, {
  AutocompleteProps,
  autocompleteClasses,
} from "@mui/material/Autocomplete"
import Popper from "@mui/material/Popper"
import CircularProgress from "@mui/material/CircularProgress"
import { styled } from "@mui/material/styles"

import i18n from "config/i18n"
import { colors } from "theme"

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface Props<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
> extends AutocompleteProps<T, Multiple, DisableClearable, FreeSolo> {
  light: boolean
}

interface AutocompleteOption {
  label: string
  value: number
}

const AutocompleteComponent = styled(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ({ label, light, loading, ...restProps }: any) => (
    <Autocomplete
      disablePortal
      disableListWrap
      clearOnBlur={false}
      clearOnEscape={false}
      PopperComponent={StyledPopper}
      isOptionEqualToValue={(option: AutocompleteOption, value) =>
        option.label === value.label
      }
      getOptionLabel={(option: AutocompleteOption) => option.label}
      filterOptions={(x) => x}
      noOptionsText={i18n.t("autocomplete.empty")}
      renderInput={(params) => (
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        <TextField
          {...params}
          variant="filled"
          label={label}
          InputProps={{
            ...params.InputProps,
            disableUnderline: true,
            endAdornment: (
              <React.Fragment>
                {loading ? (
                  <CircularProgress color="primary" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
      {...restProps}
    />
  ),
)(({ light = false }) => ({
  "& .MuiFilledInput-root": {
    border: "none",
    overflow: "hidden",
    borderRadius: 9,
    backgroundColor: light ? colors.white : colors.ghostWhite,
    "&:hover": {
      backgroundColor: light ? colors.white : colors.ghostWhite,
    },
    "&.Mui-disabled": {
      backgroundColor: light ? colors.white : colors.ghostWhite,
    },
    "&.Mui-focused": {
      backgroundColor: light ? colors.white : colors.ghostWhite,
    },
  },
  "& .MuiInputLabel-root": {
    color: colors.romanSilver,
    "&:hover": {
      color: colors.romanSilver,
    },
    "&.Mui-focused": {
      color: colors.romanSilver,
    },
  },
  "& .MuiFormHelperText-root": {
    color: colors.ruby,
    letterSpacing: "0.6px",
  },
}))

AutocompleteComponent.defaultProps = {
  light: false,
}

const StyledPopper = styled(Popper)({
  [`& .${autocompleteClasses.paper}`]: {
    boxShadow: "0px 1px 1px -1px rgb(0 0 0 / 20%)",
  },

  [`& .${autocompleteClasses.listbox}`]: {
    boxShadow: "none",
    boxSizing: "border-box",
    "& ul": {
      padding: 0,
      margin: 0,
    },
    "& li": {
      textAlign: "left",
    },
  },
})

export default AutocompleteComponent
